import TextArea from '@/components/ai/TextArea';
import Icon from '@/components/shared/Icons';
import { Button } from '@/components/shared/button/Button';
import { useCallback, useEffect, useState } from 'react';
import type { AIStrings } from '@/utils/filters/options/getFilterOptions';
import type { Locale } from '@/i18n';

type Props = {
  strings: AIStrings;
  query: string[];
  setQuery: (updatedQuery: string[]) => void;
  setTriggerSearch: (b: boolean) => void;
  setError: (e: { error: boolean; text: string }) => void;
  error: { error: boolean; text: string };
  locale: Locale;
};

export default function EditMode({
  query,
  setQuery,
  setError,
  error,
  setTriggerSearch,
  strings,
  locale
}: Props): JSX.Element {
  const [editText, setEditText] = useState<string>('');

  useEffect(() => {
    setEditText(query[0]);
  }, [query]);

  const updateQuery = useCallback(() => {
    if (editText && editText.trim() !== '') {
      setQuery([editText]);
      setTriggerSearch(true);
    }
  }, [editText, setQuery, setTriggerSearch]);

  const onClear = useCallback(
    e => {
      e.stopPropagation();
      setEditText('');
    },
    [setEditText]
  );

  return (
    <>
      {/* eslint-disable-next-line react/jsx-no-bind */}
      <TextArea
        strings={strings}
        editText={editText}
        setEditText={setEditText}
        updateQuery={updateQuery}
        setError={setError}
        error={error}
        locale={locale}
      />
      <div className="flex flex-row items-start justify-center">
        <Button
          className="m-2 -mr-1"
          icon={<Icon id="x" className="h-5 w-5" />}
          appearance="ghost"
          onClick={onClear}
          testID="ai-search-clear-button"
          aria-label={strings.clearAria}
        />
        <Button
          className="m-2 rotate-90 rounded-full bg-gradient-to-r from-[#0071F2] to-[#9B66FF]"
          icon={<Icon id="search" className="h-4 w-4 fill-background" />}
          appearance="ghost"
          onClick={updateQuery}
          testID="ai-search-trigger-search-button"
          aria-label={strings.searchAria}
        />
      </div>
    </>
  );
}
